import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/20/solid'

import usePlacowkiData from './hooks/usePlacowkiData'
import usePlacowkiActions from './hooks/usePlacowkiActions'
import usePlacowkiSelection from './hooks/usePlacowkiSelection'
import useKampanieModal from '../listKampanie/hooks/useKampanieModal'
import KampanieService from '@/services/KampanieService'
import KampanieModal from '../listKampanie/components/KampanieModal'
import SearchActionsBar from '@/widgets/SearchActionsBar'
import LoadMoreSection from '@/widgets/LoadMoreSection'
import PlacowkiTable from './components/PlacowkiTable'
import KampaniaProgressBar from './components/KampaniaProgressBar'
import PlacowkaFilterModal from '../../../../filter-modals/components/PlacowkaFilterModal'
import MiastoFilterModal from '../../../../filter-modals/components/MiastoFilterModal'
import GminaFilterModal from '../../../../filter-modals/components/GminaFilterModal'
import PowiatFilterModal from '../../../../filter-modals/components/PowiatFilterModal'
import WojewodztwoFilterModal from '../../../../filter-modals/components/WojewodztwoFilterModal'
import TypFilterModal from '../../../../filter-modals/components/TypFilterModal'
import StatusFilterModal from '../../../../filter-modals/components/StatusFilterModal'
import { TRIAGE_OPTIONS } from '../../../klienci/Placowki/common/triageCategories'

const STATUS_OPTIONS = [
	{ value: 'done', label: 'Wykonane' },
	{ value: 'undone', label: 'Niewykonane' },
]

export default function KampaniePlacowki() {
	const { kampaniaId } = useParams()
	const [kampaniaName, setKampaniaName] = useState('')
	const [kampaniaDescription, setKampaniaDescription] = useState('')
	const [kampaniaDetails, setKampaniaDetails] = useState(null)
	const [kampaniaProgress, setKampaniaProgress] = useState({ doneCount: 0, placowkaCount: 0 })
	const [loadingKampaniaDetails, setLoadingKampaniaDetails] = useState(true)
	const [isPlacowkaFilterOpen, setIsPlacowkaFilterOpen] = useState(false)
	const [selectedPlacowkaId, setSelectedPlacowkaId] = useState(null)
	const [isMiastoFilterOpen, setIsMiastoFilterOpen] = useState(false)
	const [selectedMiastoId, setSelectedMiastoId] = useState(null)
	const [isGminaFilterOpen, setIsGminaFilterOpen] = useState(false)
	const [selectedGminaId, setSelectedGminaId] = useState(null)
	const [isPowiatFilterOpen, setIsPowiatFilterOpen] = useState(false)
	const [selectedPowiatId, setSelectedPowiatId] = useState(null)
	const [isWojewodztwoFilterOpen, setIsWojewodztwoFilterOpen] = useState(false)
	const [selectedWojewodztwoId, setSelectedWojewodztwoId] = useState(null)
	const [isTypFilterOpen, setIsTypFilterOpen] = useState(false)
	const [selectedTypId, setSelectedTypId] = useState(null)
	const [isTriageFilterOpen, setIsTriageFilterOpen] = useState(false)
	const [selectedTriage, setSelectedTriage] = useState(null)
	const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false)
	const [selectedStatus, setSelectedStatus] = useState(null)

	const {
		data,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData,
		handleLoadMore,
		setSorting,
		updateFilters,
		sortColumn,
		sortDirection,
		searchQuery,
		clearError,
	} = usePlacowkiData(kampaniaId)

	const loadKampaniaDetails = useCallback(async () => {
		if (!kampaniaId) {
			setKampaniaName('')
			setKampaniaDescription('')
			setKampaniaDetails(null)
			setKampaniaProgress({ doneCount: 0, placowkaCount: 0 })
			setLoadingKampaniaDetails(false)
			return
		}

		setLoadingKampaniaDetails(true)
		const result = await KampanieService.getById(kampaniaId)
		const kampania = result?.data || null

		setKampaniaDetails(kampania)
		setKampaniaName(kampania?.nazwa || '')
		setKampaniaDescription(kampania?.description ?? '')
		setKampaniaProgress({
			doneCount: kampania?.doneCount ?? 0,
			placowkaCount: kampania?.placowkaCount ?? 0,
		})
		setLoadingKampaniaDetails(false)
	}, [kampaniaId])

	const {
		isModalOpen,
		formName,
		setFormName,
		formDescription,
		setFormDescription,
		isSaving,
		openEditModal,
		closeModal,
		handleSubmit,
	} = useKampanieModal(loadKampaniaDetails)

	const refreshData = useCallback(async () => {
		await loadData()
		await loadKampaniaDetails()
	}, [loadData, loadKampaniaDetails])

	const {
		selectedPlacowki,
		handleSelectAll,
		handleSelectPlacowka,
		clearSelection,
		getSelectionState,
	} = usePlacowkiSelection()

	const handleKampaniaUpdated = useCallback(kampania => {
		if (!kampania) return
		setKampaniaProgress({
			doneCount: kampania.doneCount ?? 0,
			placowkaCount: kampania.placowkaCount ?? 0,
		})
		if (kampania.nazwa) {
			setKampaniaName(kampania.nazwa)
		}
		setKampaniaDescription(kampania?.description ?? '')
	}, [])

	const { processingId, isRemoving, handleMarkAsDone, handleMarkAsUndone, handleRemoveSelected } =
		usePlacowkiActions(kampaniaId, refreshData, handleKampaniaUpdated)

	const triageFilterOptions = useMemo(
		() => TRIAGE_OPTIONS.map(option => ({ value: option.value, label: option.label })),
		[],
	)

	const hasTriageFilter =
		selectedTriage !== null && selectedTriage !== undefined && selectedTriage !== ''

	const filteredData = useMemo(() => {
		if (!selectedStatus) return data
		if (selectedStatus === 'done') {
			return data.filter(item => !!item?.doneDate)
		}
		return data.filter(item => !item?.doneDate)
	}, [data, selectedStatus])

	const { isAllSelected, isIndeterminate } = getSelectionState(filteredData)

	const handleRemoveSelectedFromCampaign = useCallback(() => {
		handleRemoveSelected([...selectedPlacowki], clearSelection)
	}, [selectedPlacowki, handleRemoveSelected, clearSelection])

	const actionButtons = useMemo(
		() => [
			{
				type: 'delete',
				label: `Usun z kampanii (${selectedPlacowki.size})`,
				count: selectedPlacowki.size,
				onClick: handleRemoveSelectedFromCampaign,
				loading: isRemoving,
			},
		],
		[selectedPlacowki.size, isRemoving, handleRemoveSelectedFromCampaign],
	)

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: 'Error',
				text: error,
				icon: 'error',
			}).then(() => {
				clearError()
			})
		}
	}, [error, clearError])

	useEffect(() => {
		loadData()
	}, [loadData])

	useEffect(() => {
		loadKampaniaDetails()
	}, [loadKampaniaDetails])

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}

	const handleSearchChange = e => {
		updateFilters({ searchText: e.target.value })
	}

	const handleApplyPlacowkaFilter = placowkaId => {
		updateFilters({ placowkaId: placowkaId || null })
		setSelectedPlacowkaId(placowkaId || null)
		setIsPlacowkaFilterOpen(false)
	}

	const handleApplyMiastoFilter = miastoId => {
		updateFilters({ id_miasto: miastoId || null })
		setSelectedMiastoId(miastoId || null)
		setIsMiastoFilterOpen(false)
	}

	const handleApplyGminaFilter = gminaId => {
		updateFilters({ id_gmina: gminaId || null })
		setSelectedGminaId(gminaId || null)
		setIsGminaFilterOpen(false)
	}

	const handleApplyPowiatFilter = powiatId => {
		updateFilters({ id_powiat: powiatId || null })
		setSelectedPowiatId(powiatId || null)
		setIsPowiatFilterOpen(false)
	}

	const handleApplyWojewodztwoFilter = wojewodztwoId => {
		updateFilters({ id_wojewodztwo: wojewodztwoId || null })
		setSelectedWojewodztwoId(wojewodztwoId || null)
		setIsWojewodztwoFilterOpen(false)
	}

	const handleApplyTypFilter = typId => {
		updateFilters({ id_typ: typId || null })
		setSelectedTypId(typId || null)
		setIsTypFilterOpen(false)
	}

	const handleApplyTriageFilter = triage => {
		const nextTriage =
			triage !== null && triage !== undefined && triage !== '' ? Number(triage) : null
		updateFilters({ triage: nextTriage })
		setSelectedTriage(nextTriage)
		setIsTriageFilterOpen(false)
	}

	const handleApplyStatusFilter = status => {
		setSelectedStatus(status || null)
		setIsStatusFilterOpen(false)
	}

	return (
		<>
			<div className="mb-4">
				<Link
					to="/komunikacja/kampanie"
					className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline">
					<ArrowLeftIcon className="h-4 w-4" />
					<span>Powrot do kampanii</span>
				</Link>
				<div className="mt-2 flex items-start justify-between gap-4">
					<div className="min-w-0 flex-1">
						<h1 className="text-xl font-semibold text-gray-900">
							{kampaniaName || 'Placowki kampanii'}
						</h1>
						{loadingKampaniaDetails ? (
							<p className="mt-2 text-sm text-gray-500">Ladowanie opisu...</p>
						) : kampaniaDescription ? (
							<p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">{kampaniaDescription}</p>
						) : (
							<p className="mt-2 text-sm italic text-gray-400">Brak opisu kampanii.</p>
						)}
					</div>
					{kampaniaDetails?._id ? (
						<button
							type="button"
							onClick={() => openEditModal(kampaniaDetails)}
							className="inline-flex shrink-0 items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100">
							<PencilIcon className="h-4 w-4" />
							<span>Edytuj</span>
						</button>
					) : null}
				</div>
			</div>

			<KampaniaProgressBar
				doneCount={kampaniaProgress.doneCount}
				placowkaCount={kampaniaProgress.placowkaCount}
				loading={loadingKampaniaDetails}
			/>

			<SearchActionsBar
				value={searchQuery}
				onChange={handleSearchChange}
				placeholder="Szukaj placowki po nazwie..."
				buttons={actionButtons}
			/>

			<PlacowkiTable
				data={filteredData}
				loading={loading}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
				selectedPlacowki={selectedPlacowki}
				onSelectAll={checked => handleSelectAll(checked, filteredData)}
				onSelectPlacowka={handleSelectPlacowka}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				onMarkAsDone={handleMarkAsDone}
				onMarkAsUndone={handleMarkAsUndone}
				processingId={processingId}
				hasPlacowkaFilter={!!selectedPlacowkaId}
				onOpenPlacowkaFilter={() => setIsPlacowkaFilterOpen(true)}
				hasMiastoFilter={!!selectedMiastoId}
				hasGminaFilter={!!selectedGminaId}
				hasPowiatFilter={!!selectedPowiatId}
				hasWojewodztwoFilter={!!selectedWojewodztwoId}
				hasTypFilter={!!selectedTypId}
				hasTriageFilter={hasTriageFilter}
				hasStatusFilter={!!selectedStatus}
				onOpenMiastoFilter={() => setIsMiastoFilterOpen(true)}
				onOpenGminaFilter={() => setIsGminaFilterOpen(true)}
				onOpenPowiatFilter={() => setIsPowiatFilterOpen(true)}
				onOpenWojewodztwoFilter={() => setIsWojewodztwoFilterOpen(true)}
				onOpenTypFilter={() => setIsTypFilterOpen(true)}
				onOpenTriageFilter={() => setIsTriageFilterOpen(true)}
				onOpenStatusFilter={() => setIsStatusFilterOpen(true)}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={filteredData.length}
				totalCount={selectedStatus ? filteredData.length : totalCount}
			/>

			<KampanieModal
				isOpen={isModalOpen}
				formName={formName}
				setFormName={setFormName}
				formDescription={formDescription}
				setFormDescription={setFormDescription}
				isSaving={isSaving}
				onClose={closeModal}
				onSubmit={handleSubmit}
			/>
			<PlacowkaFilterModal
				isOpen={isPlacowkaFilterOpen}
				onClose={() => setIsPlacowkaFilterOpen(false)}
				selectedPlacowkaId={selectedPlacowkaId}
				onApply={handleApplyPlacowkaFilter}
				title="Filtruj po placowce"
				description="Wybierz placowke, aby zawezic liste."
				applyLabel="Zastosuj"
				clearLabel="Wyczysc filtr"
			/>
			<MiastoFilterModal
				isOpen={isMiastoFilterOpen}
				onClose={() => setIsMiastoFilterOpen(false)}
				selectedMiastoId={selectedMiastoId}
				idGmina={selectedGminaId}
				idPowiat={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyMiastoFilter}
			/>
			<GminaFilterModal
				isOpen={isGminaFilterOpen}
				onClose={() => setIsGminaFilterOpen(false)}
				selectedGminaId={selectedGminaId}
				idPowiat={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyGminaFilter}
			/>
			<PowiatFilterModal
				isOpen={isPowiatFilterOpen}
				onClose={() => setIsPowiatFilterOpen(false)}
				selectedPowiatId={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyPowiatFilter}
			/>
			<WojewodztwoFilterModal
				isOpen={isWojewodztwoFilterOpen}
				onClose={() => setIsWojewodztwoFilterOpen(false)}
				selectedWojewodztwoId={selectedWojewodztwoId}
				onApply={handleApplyWojewodztwoFilter}
			/>
			<TypFilterModal
				isOpen={isTypFilterOpen}
				onClose={() => setIsTypFilterOpen(false)}
				selectedTypId={selectedTypId}
				onApply={handleApplyTypFilter}
			/>
			<StatusFilterModal
				isOpen={isTriageFilterOpen}
				onClose={() => setIsTriageFilterOpen(false)}
				selectedStatus={selectedTriage}
				onApply={handleApplyTriageFilter}
				statusOptions={triageFilterOptions}
				title="Filtruj po kategorii klienta"
				description="Wybierz kategorie klienta, aby zawezic liste placowek."
				applyLabel="Zastosuj"
				clearLabel="Wyczysc filtr"
			/>
			<StatusFilterModal
				isOpen={isStatusFilterOpen}
				onClose={() => setIsStatusFilterOpen(false)}
				selectedStatus={selectedStatus}
				onApply={handleApplyStatusFilter}
				statusOptions={STATUS_OPTIONS}
				title="Filtruj po statusie"
				description="Wybierz status wykonania placowki."
				applyLabel="Zastosuj"
				clearLabel="Wyczysc filtr"
			/>
		</>
	)
}
